import type { SaveStatus } from '@/components/SaveStatusIndicator';

export const PROFILE_AUTOSAVE_DELAY_MS = 1000;

export interface ProfileAutosaveController {
  schedule: () => void;
  flush: () => Promise<void>;
}

/** Only a response for the latest local revision may hydrate or clear the draft. */
export function shouldApplyAutosaveResult(
  autosave: boolean,
  submittedRevision: number,
  currentRevision: number,
): boolean {
  return !autosave || submittedRevision === currentRevision;
}

export function statusAfterAutosave(
  saved: boolean,
  submittedRevision: number,
  currentRevision: number,
  currentStatus: SaveStatus,
): SaveStatus {
  if (saved) {
    return submittedRevision === currentRevision ? 'saved' : currentStatus;
  }
  return currentStatus === 'saving' ? 'error' : currentStatus;
}

export function sameLocationSelection(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/** Required-field schemas only gate a save when that form was actually edited. */
export function shouldValidateDirtyForm(formDirty: boolean): boolean {
  return formDirty;
}

/**
 * Skip the Practitioner write when nothing changed, unless a prior in-flight
 * autosave may have persisted an intermediate draft that this pass must revert.
 */
export function shouldSkipUnchangedProviderSave(
  dirty: boolean,
  forcePersist: boolean,
): boolean {
  return !dirty && !forcePersist;
}

/** Debounces profile writes and serializes a follow-up when edits arrive during a save. */
export function createProfileAutosaveController(
  save: (forcePersist: boolean) => Promise<boolean>,
  delayMs = PROFILE_AUTOSAVE_DELAY_MS,
): ProfileAutosaveController {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  let queued = false;
  let pending = false;
  let forceNext = false;

  const run = async (): Promise<void> => {
    if (inFlight) {
      queued = true;
      await inFlight;
      return;
    }

    const currentRun = (async () => {
      let forcePersist = forceNext;
      forceNext = false;
      let lastSaved = false;
      do {
        queued = false;
        lastSaved = await save(forcePersist);
        if (lastSaved) forcePersist = true;
        else if (forcePersist) forceNext = true;
      } while (queued);
      if (lastSaved && !queued && !timer) pending = false;
      else if (lastSaved && timer) forceNext = true;
    })();
    inFlight = currentRun;
    try {
      await currentRun;
    } finally {
      if (inFlight === currentRun) inFlight = null;
    }
  };

  return {
    schedule: () => {
      pending = true;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void run();
      }, delayMs);
    },
    flush: async () => {
      const hadTimer = Boolean(timer);
      if (timer) {
        clearTimeout(timer);
        timer = null;
        pending = true;
      }
      if (inFlight) {
        if (hadTimer) queued = true;
        await inFlight;
      }
      if (timer) {
        clearTimeout(timer);
        timer = null;
        pending = true;
      }
      if (pending) {
        await run();
      }
    },
  };
}
